export type RecipientVisualStatusCode = 'not-sent' | 'sent' | 'pending' | 'active' | 'inactive';

export interface RecipientStatusSource {
  bank_account?: unknown;
  recipient_id?: string;
  recipient_info?: {
    recipient_id?: string;
    id?: string;
    status?: string;
    kyc_status?: string;
    bank_account_status?: string;
    kyc_details?: {
      status?: string;
      status_reason?: string;
    };
    default_bank_account?: {
      status?: string;
    };
  };
  onboard_qrcode?: {
    url?: string;
    expires_at?: string;
  };
  status?: string;
}

export interface RecipientVisualStatus {
  code: RecipientVisualStatusCode;
  label: string;
  description: string;
  nextStep: string;
  recipientLabel: string;
  kycLabel: string;
  bankAccountLabel: string;
  onboardingLabel: string;
}

export function isRecipientReadyForPayments(settings?: RecipientStatusSource): boolean {
  const recipient = settings?.recipient_info;
  const recipientId = settings?.recipient_id || recipient?.recipient_id || recipient?.id;

  return !!recipientId && normalizeStatus(recipient?.status || settings?.status) === 'active';
}

const activeStatuses = ['active', 'approved', 'approved_with_issues', 'success'];
const pendingStatuses = [
  'pending',
  'pending_review',
  'waiting',
  'waiting_documents',
  'under_review',
  'analysis',
  'processing',
  'registration',
  'registering',
  'created',
];
const inactiveStatuses = ['inactive', 'rejected', 'refused', 'failed', 'denied', 'canceled', 'cancelled', 'blocked', 'suspended'];

export function buildRecipientVisualStatus(settings?: RecipientStatusSource): RecipientVisualStatus {
  const recipient = settings?.recipient_info;
  const recipientId = recipient?.recipient_id || recipient?.id;
  const recipientStatus = normalizeStatus(settings?.status || recipient?.status);
  const kycStatus = normalizeStatus(recipient?.kyc_details?.status || recipient?.kyc_status);
  const bankAccountStatus = normalizeStatus(recipient?.default_bank_account?.status || recipient?.bank_account_status);
  const knownStatuses = [recipientStatus, kycStatus, bankAccountStatus].filter(Boolean);
  const hasBankAccount = !!settings?.bank_account;
  const hasOnboarding = !!settings?.onboard_qrcode?.url;

  if (!hasBankAccount) {
    return statusView(
      'not-sent',
      'Nao enviado',
      'Preencha e salve os dados bancarios para iniciar o cadastro como recebedor.',
      'Salve os dados bancarios.',
      recipientStatus,
      kycStatus,
      bankAccountStatus,
      settings,
    );
  }

  if (!recipientId) {
    return statusView(
      'sent',
      'Dados enviados',
      'Os dados bancarios foram salvos e aguardam criacao do recebedor na Pagar.me.',
      'Aguarde a criacao do recebedor ou salve novamente se necessario.',
      recipientStatus,
      kycStatus,
      bankAccountStatus,
      settings,
    );
  }

  if (knownStatuses.some((status) => inactiveStatuses.includes(status))) {
    return statusView(
      'inactive',
      'Inativo',
      'O cadastro do recebedor precisa de correcao ou nova validacao.',
      'Revise os dados bancarios e abra o cadastro Pagar.me.',
      recipientStatus,
      kycStatus,
      bankAccountStatus,
      settings,
    );
  }

  if (isActiveRecipient(recipientStatus, kycStatus, bankAccountStatus)) {
    return statusView(
      'active',
      'Ativo',
      'O recebedor esta validado para pagamentos online.',
      'Recebedor disponivel para receber pagamentos online.',
      recipientStatus,
      kycStatus,
      bankAccountStatus,
      settings,
    );
  }

  if (hasOnboarding || knownStatuses.some((status) => pendingStatuses.includes(status))) {
    return statusView(
      'pending',
      'Pendente',
      'O cadastro foi iniciado e aguarda conclusao ou validacao da Pagar.me.',
      'Abra o cadastro Pagar.me para concluir etapas pendentes.',
      recipientStatus,
      kycStatus,
      bankAccountStatus,
      settings,
    );
  }

  return statusView(
    'pending',
    'Pendente',
    'O recebedor foi criado e aguarda atualizacao da validacao.',
    'Abra o cadastro Pagar.me para acompanhar ou concluir etapas pendentes.',
    recipientStatus,
    kycStatus,
    bankAccountStatus,
    settings,
  );
}

function statusView(
  code: RecipientVisualStatusCode,
  label: string,
  description: string,
  nextStep: string,
  recipientStatus: string,
  kycStatus: string,
  bankAccountStatus: string,
  settings?: RecipientStatusSource,
): RecipientVisualStatus {
  return {
    code,
    label,
    description,
    nextStep,
    recipientLabel: displayStatus(recipientStatus),
    kycLabel: displayStatus(kycStatus),
    bankAccountLabel: displayStatus(bankAccountStatus),
    onboardingLabel: code === 'active' ? 'Concluido' : displayOnboarding(settings?.onboard_qrcode),
  };
}

function isActiveRecipient(recipientStatus: string, kycStatus: string, bankAccountStatus: string): boolean {
  const recipientIsActive = activeStatuses.includes(recipientStatus);
  const kycIsActive = !kycStatus || activeStatuses.includes(kycStatus);
  const bankAccountIsActive = !bankAccountStatus || activeStatuses.includes(bankAccountStatus);

  return recipientIsActive && kycIsActive && bankAccountIsActive;
}

function displayOnboarding(onboarding?: RecipientStatusSource['onboard_qrcode']): string {
  if (!onboarding?.url) {
    return 'Nao gerado';
  }

  if (!onboarding.expires_at) {
    return 'Disponivel';
  }

  const expiresAt = new Date(onboarding.expires_at);

  if (Number.isNaN(expiresAt.getTime())) {
    return 'Disponivel';
  }

  return Date.now() >= expiresAt.getTime() ? 'Expirado' : 'Disponivel';
}

function displayStatus(status: string): string {
  const statusLabels: Record<string, string> = {
    active: 'Ativo',
    approved: 'Aprovado',
    approved_with_issues: 'Aprovado com pendencias',
    success: 'Aprovado',
    pending: 'Pendente',
    pending_review: 'Em analise',
    waiting: 'Aguardando',
    waiting_documents: 'Aguardando documentos',
    under_review: 'Em analise',
    analysis: 'Em analise',
    processing: 'Processando',
    registration: 'Cadastro enviado',
    registering: 'Cadastro enviado',
    created: 'Criado',
    inactive: 'Inativo',
    rejected: 'Rejeitado',
    refused: 'Recusado',
    failed: 'Falhou',
    denied: 'Negado',
    canceled: 'Cancelado',
    cancelled: 'Cancelado',
    blocked: 'Bloqueado',
    suspended: 'Suspenso',
  };

  return statusLabels[status] || 'Nao informado';
}

function normalizeStatus(status?: string): string {
  return String(status || '')
    .trim()
    .toLowerCase();
}
